import { Button, IconCheck, useLocalization } from '@magicdoorinc/ui';
import { useLocation, useNavigate } from '@solidjs/router';
import { createEffect, createMemo, Show } from 'solid-js';
import { onboardingV3StepsDict } from '~/components/Onboarding/V3/config';
import { useOnboarding, useTrialStatus } from '~/contexts/global';
import { isDemo } from '~/utils/constant';
import type { OnboardingV3Status } from '~/utils/constant';

interface CompletionBarProps {
  title?: string;
  subtitle?: string;
}

export const CompletionBar = (props: CompletionBarProps) => {
  const { t } = useLocalization();
  const { companies, hideOnboarding } = useOnboarding();
  const location = useLocation();
  const navigate = useNavigate();

  const isCompleted = createMemo(() => {
    const status = companies()?.onboardingStatus as OnboardingV3Status;
    const lastStep = onboardingV3StepsDict.getOptions().at(-1)?.value;
    return status === lastStep;
  });

  return (
    <div class="relative flex flex-col rounded-lg border bg-white p-3 md:p-5">
      <div class="flex flex-col gap-3 md:flex-row md:items-center md:justify-between md:gap-0">
        <div class="flex-1">
          <h3 class="text-sm font-semibold capitalize md:text-base">{props.title || t('Complete steps to start using your account')}!</h3>
          <p class="mt-1 text-xs text-primary md:text-sm">{props.subtitle || t('Enable 50+ features by completing onboarding')}</p>
        </div>
        <div class="flex items-center justify-end gap-2 md:shrink-0">
          <Show when={!location.pathname.startsWith('/onboarding')}>
            <Button variant="outlined" rounded="full" size="sm" onClick={() => navigate('/onboarding')} class="text-xs md:text-sm">
              {t('Continue onboarding')}
            </Button>
          </Show>

          <Show when={isCompleted()}>
            <Button
              rounded="full"
              size="sm"
              loading={hideOnboarding.loading}
              onClick={() => {
                hideOnboarding();
              }}
              class="text-xs md:text-sm">
              <IconCheck class="size-3.5 md:size-4" />
              {t("I'm done with onboarding")}
            </Button>
          </Show>
        </div>
      </div>
    </div>
  );
};

export const CompletionBarInNavigation = () => {
  const { companies, isOnboardingCompleted, isExperienceOnboardingActive, shouldHideOnboardingMenu } = useOnboarding();
  const { shouldShowTrialBanner } = useTrialStatus();
  const location = useLocation();

  const shouldShow = createMemo(() => {
    return (
      companies() &&
      !location.pathname.startsWith('/onboarding') &&
      !isOnboardingCompleted() &&
      !isExperienceOnboardingActive() &&
      !isDemo() &&
      !shouldHideOnboardingMenu()
    );
  });

  createEffect(() => {
    const isMobile = window.innerWidth < 768;
    const trialBannerHeight = shouldShowTrialBanner() ? (isMobile ? '100px' : '50px') : '0px';
    if (shouldShow()) {
      const completionBarHeight = isMobile ? '120px' : '160px';
      document.documentElement.style.setProperty('--banner-height', `calc(${completionBarHeight} + ${trialBannerHeight})`);
    } else {
      document.documentElement.style.setProperty('--banner-height', `calc(50px + ${trialBannerHeight})`);
    }
  });

  return (
    <Show when={shouldShow()}>
      <CompletionBar />
    </Show>
  );
};
